/** Geometry helpers for authoring dungeons. All outputs are in yards. */
import type { NpcId, Polygon, Radians, Rect, SpawnPoint, Vec2 } from '@keystone/sim';

const round1 = (v: number): number => Math.round(v * 10) / 10;

/** Make a converter from source-map units to yards. */
export function mapScale(yardsPerUnit: number) {
  const pt = (x: number, y: number): Vec2 => ({ x: round1(x * yardsPerUnit), y: round1(y * yardsPerUnit) });
  return {
    yardsPerUnit,
    pt,
    /** Axis-aligned rectangle from two map-space corners, as a CCW polygon. */
    rectPoly(x0: number, y0: number, x1: number, y1: number): Polygon {
      return { points: [pt(x0, y0), pt(x1, y0), pt(x1, y1), pt(x0, y1)] };
    },
    rect(x0: number, y0: number, x1: number, y1: number): Rect {
      const a = pt(x0, y0);
      const b = pt(x1, y1);
      return { x: a.x, y: a.y, w: round1(b.x - a.x), h: round1(b.y - a.y) };
    },
    /** A walkable strip `widthYd` wide along the segment a→b (map units). */
    strip(a: [number, number], b: [number, number], widthYd: number): Polygon {
      return stripPoly(pt(a[0], a[1]), pt(b[0], b[1]), widthYd);
    },
  };
}

/** Rotated rectangle along a→b (yards), counter-clockwise. */
export function stripPoly(a: Vec2, b: Vec2, width: number): Polygon {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (width / 2);
  const ny = (dx / len) * (width / 2);
  const points = [
    { x: round1(a.x - nx), y: round1(a.y - ny) },
    { x: round1(b.x - nx), y: round1(b.y - ny) },
    { x: round1(b.x + nx), y: round1(b.y + ny) },
    { x: round1(a.x + nx), y: round1(a.y + ny) },
  ];
  return { points: signedArea(points) < 0 ? points.reverse() : points };
}

export function signedArea(points: readonly Vec2[]): number {
  let s = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    s += p.x * q.y - q.x * p.y;
  }
  return s / 2;
}

/** Point-in-polygon (ray casting). Points on the edge may go either way. */
export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  let inside = false;
  const pts = poly.points;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

export const angleTo = (from: Vec2, to: Vec2): Radians => Math.atan2(to.y - from.y, to.x - from.x);

/**
 * Place a pack: the first NPC stands at `center`, the rest on a ring around it.
 * Everyone faces `faceTo` (usually where the group comes from).
 */
export function cluster(center: Vec2, npcIds: readonly NpcId[], faceTo: Vec2, ringRadius = 3.5): SpawnPoint[] {
  const facing = round3(angleTo(center, faceTo));
  const ring = npcIds.length - 1;
  return npcIds.map((npcId, i) => {
    if (i === 0) return { npcId, pos: center, facing };
    const a = facing + Math.PI / 2 + ((i - 1) * 2 * Math.PI) / ring;
    return { npcId, pos: { x: round1(center.x + Math.cos(a) * ringRadius), y: round1(center.y + Math.sin(a) * ringRadius) }, facing };
  });
}

const round3 = (v: number): number => Math.round(v * 1000) / 1000;
